import React, { useEffect, useState } from 'react';
import './isaTransferPage.scss';
import Accordion from '../../Accordion/Accordion';
import Button from '../../Form/Button/Button';
import envConfig from '../../../config/env-config';
import checkMint from '../../../assets/images/check-mint.svg';
// @ts-ignore - image will be present once copied from flexi-product-bff
import heroImage from '../../../assets/images/Transfer_your_savings_to_Investec_Save.jpg';
import { setPageTitle, setMetaDescription } from '../../../utils/helpers';
import { useUnifiedAnalytics } from '../../../contexts/AnalyticsWrapper';
import withFeatureFlags from '../../FeatureFlags/withFeatureFlags';
/** @import {WithFeatureFlagsProps} from '../../FeatureFlags/FeatureFlagsTypes' */

const WHY_FEATURES = [
    {
        heading: 'A competitive fixed rate',
        body: 'Earn a guaranteed rate for the full term.',
    },
    {
        heading: 'Keep your tax benefits',
        body: 'All Cash ISA money, from this year and previous years, stays within the tax wrapper.',
    },
    {
        heading: 'Partial or full transfers',
        body: 'Transfer everything at once, or a portion of the money you have saved in previous tax years.',
    },
    {
        heading: 'Simple, guided process',
        body: 'We handle the transfer for you and keep you updated.',
    },
];

const HOW_STEPS = [
    {
        heading: 'Open an Investec Cash ISA',
        body: 'Apply online in minutes.',
    },
    {
        heading: 'Have your current ISA details ready',
        body: 'Name of provider, sort code and account number, and/or roll number, for your existing ISA.',
    },
    {
        heading: 'Choose what to transfer',
        body: 'Transfer your full balance or a partial amount. Current tax year funds must be transferred in full.',
    },
    {
        heading: 'Check your details',
        body: "Make sure the name and personal details on the ISA you're transferring match your ID to avoid delays.",
    },
    {
        heading: 'Leave the rest to us',
        body: "We'll contact your current provider and manage the transfer for you while keeping you updated.",
    },
];

const BEFORE_YOU_TRANSFER = [
    'We only accept transfers from certain providers - please check the full list below.',
    'We only accept transfers from other Cash ISAs.',
    "Transfer one Cash ISA per Fixed Rate Cash ISA account. To transfer more than one, you'll need to open additional Fixed Rate Cash ISAs.",
];

/**
 * @param {any[]} array
 * @param {number} columns
 */
const splitIntoColumns = (array, columns) => {
    const size = Math.ceil(array.length / columns);
    return Array.from({ length: columns }, (_, i) => array.slice(i * size, i * size + size));
};

const FAQ_ITEMS = [
    {
        heading: 'Will my ISA allowance be affected?',
        body: "Transferring a Cash ISA from previous tax years will not affect your ISA allowance. If you transfer a Cash ISA you've paid into in the current tax year, the amount you've already subscribed still counts towards your annual allowance.",
    },
    {
        heading: 'Can I transfer part of an ISA?',
        body: "Yes – if your current ISA provider allows partial transfers. However, if you've already paid into your ISA during the current tax year, you must transfer at least the amount you've paid in.",
    },
    {
        heading: 'Are there any fees for transferring my ISA?',
        body: "We don't charge a fee to receive an ISA transfer. However, your current provider may apply early closure fees or other transfer charges. Please check any fees or transfer limitations with your current provider before you start your transfer.",
    },
    {
        heading: 'How long will my ISA transfer take?',
        body: "Cash ISA transfers can take up to 15 business days, depending on your provider. We'll keep you updated and let you know as soon as your funds arrive.",
    },
    {
        heading: 'What if my current provider delays the transfer?',
        body: "If there's a delay, we'll monitor the transfer and keep you updated where possible. Your money stays with your existing provider until the transfer completes, and your ISA tax benefits remain protected.",
    },
    {
        heading: 'What happens if I go over my ISA annual limit with Investec Save?',
        body: "If you pay in more than your annual ISA allowance, the extra amount won't be eligible for ISA tax benefits. We'll return the oversubscribed amount, along with any interest earned, to your linked account.",
    },
];

const IsaTransferPage = (/** @type {WithFeatureFlagsProps} */ { featureEnabled }) => {
    const analytics = useUnifiedAnalytics();
    const [providers, setProviders] = useState(/** @type {{id: string, name: string, serviceUserNumber: string}[]} */([]));
    const secondaryAccTransferDisabled = typeof featureEnabled === 'function' ? featureEnabled('SecondaryAccountFISATransferDisabled') : true;
    useEffect(() => {
        setPageTitle('Tax-free savings transfer your ISA to us | Investec Save');
        setMetaDescription('Transfer your existing Cash ISA to Investec Save tax-free. Keep your tax wrapper, earn a competitive fixed rate, and let us handle the transfer for you.');
        if (analytics) {
            analytics.trackAnalyticsPageEvent(
                'isa transfer page load',
                {},
                'ISA Transfer Page',
                'ISA_Transfer_Page|Page_Load|ISA_Transfer'
            );
        }
    }, []);

    useEffect(() => {
        const fetchProviders = async () => {
            try {
                const response = await fetch('/api/isa-transfer-providers');

                if (!response.ok) {
                    throw new Error(`Failed to fetch ISA transfer providers: ${response.status}`);
                }

                const data = await response.json();
                setProviders(data);
            } catch (error) {
                console.error('Error fetching ISA transfer providers:', error);
                setProviders([]);
            }
        };

        fetchProviders();
    }, []);

    const redirectToFAQs = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'isa transfer page view faqs click',
                `${envConfig.links.productPageUrl}/help`,
                'help',
                '',
                '',
                {},
                'button click',
                'ISA_Transfer_Page|Button_Click|View_Our_FAQs',
                'ISA Transfer Page'
            );
        }
       window.location.href = `${envConfig.links.productPageUrl}/help`;
    };

    const openLiveChat = () => {
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'isa transfer page live chat click',
                '',
                'help',
                '',
                '',
                {},
                'button click',
                'ISA_Transfer_Page|Button_Click|Start_Live_Chat',
                'ISA Transfer Page'
            );
        }
        /** @type {any} */
        const globalWindow = window;
        if (globalWindow.Microsoft && globalWindow.Microsoft.Omnichannel && globalWindow.Microsoft.Omnichannel.LiveChatWidget) {
            try {
                globalWindow.Microsoft.Omnichannel.LiveChatWidget.SDK.startChat();
            } catch (error) {
                console.error('Error starting LiveChat:', error);
            }
        }
    };

    const goToTransferForm = () => {
        const base = envConfig.links.applyFormLink;
        const path = 'existing-customer/apply/transfer?p=fisa-1yr';
        const qIndex = base.indexOf('?');
        const url = qIndex > 0
            ? base.substring(0, qIndex) + '/' + path + base.substring(qIndex)
            : base + '/' + path;
        if (analytics) {
            analytics.trackAnalyticsClickEvent(
                'isa transfer page start transfer click',
                url,
                'hero',
                'Fixed Rate Cash ISA',
                '',
                {},
                'link click',
                'ISA_Transfer_Page|Button_Click|Start_Your_Transfer',
                'ISA Transfer Page'
            );
        }
        window.location.href = url;
    };

    return (
        <div className="isa-transfer-page">
            {/* Hero */}
            <section className="itp-hero">
                <div className="itp-hero__left">
                    <h1 className="itp-hero__heading">Transfer your ISA to us</h1>
                    <p className="itp-hero__subtitle">
                        Transfer your existing Cash ISA to Investec Save and keep your tax wrapper.
                    </p>
                    <Button
                        label="Start your transfer"
                        customClassNames={['itp-hero__cta']}
                        disabled={secondaryAccTransferDisabled}
                        onClick={secondaryAccTransferDisabled ? undefined : goToTransferForm}
                    />
                    {secondaryAccTransferDisabled && (
                        <div className="funding-source-selection__notice itp-hero__notice">
                            <div className="funding-source-selection__info-icon">
                                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12.8104 1.66837C12.2931 1.43834 11.7025 1.43834 11.1852 1.66837C10.7856 1.84602 10.5261 2.15894 10.3455 2.41391C10.1679 2.66461 9.97784 2.99303 9.76877 3.35422L1.50161 17.6339C1.2917 17.9964 1.10095 18.3258 0.971609 18.6054C0.840159 18.8895 0.697554 19.2714 0.743003 19.7074C0.801814 20.2715 1.09736 20.7841 1.55607 21.1176C1.91057 21.3753 2.31256 21.4433 2.62432 21.4719C2.93107 21.5 3.31173 21.5 3.73061 21.5H20.2649C20.6838 21.5 21.0645 21.5 21.3712 21.4719C21.683 21.4433 22.085 21.3753 22.4395 21.1176C22.8982 20.7841 23.1937 20.2715 23.2525 19.7074C23.298 19.2714 23.1554 18.8895 23.0239 18.6054C22.8946 18.3258 22.7039 17.9964 22.494 17.6339L14.2268 3.35419C14.0177 2.99301 13.8276 2.66459 13.65 2.41391C13.4694 2.15894 13.2099 1.84602 12.8104 1.66837ZM12.9978 9V13C12.9978 13.5523 12.5501 14 11.9978 14C11.4455 14 10.9978 13.5523 10.9978 13V9C10.9978 8.44772 11.4455 8 11.9978 8C12.5501 8 12.9978 8.44772 12.9978 9ZM11.9978 16H12.0078C12.5601 16 13.0078 16.4477 13.0078 17C13.0078 17.5523 12.5601 18 12.0078 18H11.9978C11.4455 18 10.9978 17.5523 10.9978 17C10.9978 16.4477 11.4455 16 11.9978 16Z" fill="#BE732F"/>
                                </svg>
                            </div>
                            <div className="funding-source-selection__info-content">
                                <div className="funding-source-selection__notice-title">Transfers are temporarily unavailable</div>
                                <div className="funding-source-selection__notice-body">We're sorry for any inconvenience caused. If you would like to transfer your ISA to Investec Save, please check back for when transfers are available again.</div>
                            </div>
                        </div>
                    )}
                </div>
                <div className="itp-hero__right">
                    <div className="itp-hero__image-wrap">
                        <img src={heroImage} alt="" className="itp-hero__image" aria-hidden="true" />
                    </div>
                </div>
            </section>
            <section className="itp-what-is">
                <div className="itp-what-is__inner">
                    <h2 className="itp-section-heading itp-section-heading--centered">What is an ISA transfer?</h2>
                    <p className="itp-what-is__body">
                        An ISA transfer is when you move an existing Cash ISA from one provider to another without
                        losing any of its existing benefits. You might transfer to get a better rate, move your savings
                        to one provider, or switch to a different type of ISA. Your money stays within the ISA tax
                        wrapper throughout the process.
                    </p>
                </div>
            </section>

            {/* Why transfer to us? */}
            <section className="itp-why">
                <div className="itp-why__inner">
                    <h2 className="itp-section-heading itp-section-heading--centered">Why transfer to us?</h2>
                    <div className="itp-features">
                        {WHY_FEATURES.map(({ heading, body }) => (
                            <div key={heading} className="itp-feature-card">
                                <h3 className="itp-feature-card__heading">{heading}</h3>
                                <p className="itp-feature-card__body">{body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* How do I transfer? */}
            <section className="itp-how">
                <div className="itp-how__inner">
                    <h2 className="itp-section-heading itp-section-heading--centered">
                        How do I transfer my Cash ISA?
                    </h2>
                    <p className="itp-how__sub-heading">
                        To transfer your Cash ISA to Investec Save, you&apos;ll need to:
                    </p>

                    <div className="itp-steps">
                        {HOW_STEPS.map(({ heading, body }, i) => (
                            <React.Fragment key={heading}>
                                <div className="itp-step">
                                    <p className="itp-step__heading">{heading}</p>
                                    <p className="itp-step__body">{body}</p>
                                </div>
                                {i < HOW_STEPS.length - 1 && (
                                    <div className="itp-steps__arrow" aria-hidden="true">
                                        <svg width="19" height="42" viewBox="0 0 19 42" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M1 41L17 21L1 1" stroke="#9AC3C5" strokeWidth="2" strokeLinecap="round"/>
                                        </svg>
                                    </div>
                                )}
                            </React.Fragment>
                        ))}
                    </div>

                    <div className="itp-checklist">
                        <h3 className="itp-checklist__heading">
                            Before you transfer your Cash ISA, you should know:
                        </h3>
                        <ul className="itp-checklist__list">
                            {BEFORE_YOU_TRANSFER.map((item) => (
                                <li key={item} className="itp-checklist__item">
                                    <img src={checkMint} alt="" className="itp-checklist__icon" aria-hidden="true" />
                                    <span>{item}</span>
                                </li>
                            ))}
                        </ul>
                    </div>

                    <div className="itp-providers-accordion">
                        <Accordion
                            items={[{
                                title: 'Which providers can I transfer from?',
                                body: providers.length > 0 ? (
                                    <div className="itp-providers-list">
                                        <ul className="itp-providers-list__mobile">
                                            {providers.map(provider => <li key={provider.id}>{provider.name}</li>)}
                                        </ul>
                                        {splitIntoColumns(providers, 3).map((column, index) => (
                                            <ul key={index} className="itp-providers-list__column">
                                                {column.map(provider => <li key={provider.id}>{provider.name}</li>)}
                                            </ul>
                                        ))}
                                    </div>
                                ) : (
                                    <p>Please contact us to find out which providers are currently accepted for Cash ISA transfers.</p>
                                ),
                            }]}
                            onExpand={() => {}}
                        />
                    </div>
                </div>
            </section>

            {/* Cash ISA transfer rules and limits */}
            <section className="itp-faqs">
                <div className="itp-faqs__inner">
                    <h2 className="itp-section-heading itp-section-heading--centered">
                        Cash ISA transfer rules and limits
                    </h2>
                    <div className="itp-faq-grid">
                        {FAQ_ITEMS.map(({ heading, body }) => (
                            <div key={heading} className="itp-faq-card">
                                <h3 className="itp-faq-card__heading">{heading}</h3>
                                <p className="itp-faq-card__body">{body}</p>
                                <div className="itp-faq-card__divider" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Help section */}
            <section className="itp-help">
                <p className="itp-help__text">
                    If you still have questions, or have accessibility needs, please contact us.
                </p>
                <div className="itp-help__actions">
                    <Button variant="tertiary" label="View our FAQs" onClick={redirectToFAQs} />
                    <Button variant="secondary" label="Start live chat" onClick={openLiveChat} />
                </div>
            </section>
        </div>
    );
};

export default withFeatureFlags(IsaTransferPage);
